import { InvoiceRecord, TemplateEInvoice } from 'models/EInvoice';
import { QueryParams, TAG_TYPES } from '../types';
import { baseApi } from './index';
import { getQueryParams } from 'utils';
import { EInvoiceResponse } from 'types/invoice.type';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/e-invoices`;
const tagType = TAG_TYPES.E_INVOICE;

const eInvoiceApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllEInvoices: builder.query<
      {
        data: {
          rows: InvoiceRecord[];
          total: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query || {});
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),
    getAllTemplateEInvoices: builder.query<
      {
        data: {
          templates: TemplateEInvoice[];
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query || {});
        return {
          url: `${urlEndpoint}/templates?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.templates || []).map(({ khhDon }) => ({ type: tagType, khhDon })),
              { type: tagType, id: 'LIST' },
            ]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),
    getEInvoiceById: builder.query<
      {
        success: boolean;
        data: {
          invoice: InvoiceRecord;
        };
      },
      { id: string; query?: QueryParams }
    >({
      query: ({ id, query }) => {
        const queryString = query ? `?${getQueryParams(query || {})}` : '';

        return {
          url: `${urlEndpoint}/${id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { id }) => [{ type: tagType, id }],
    }),

    getReplaceableInvoices: builder.query<
      {
        data: {
          rows: InvoiceRecord[];
          total: number;
        };
        success: boolean;
      },
      { query?: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query || {});
        return {
          url: `${urlEndpoint}/replace?${query}`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 0,
    }),

    downloadEInvoice: builder.mutation<EInvoiceResponse, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}/download`,
        method: 'POST',
      }),
    }),
    createEInvoice: builder.mutation<EInvoiceResponse, Omit<InvoiceRecord, 'id'>>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: tagType, id: 'CREATE' },
        { type: tagType, id: 'LIST' },
      ],
    }),

    replaceEInvoice: builder.mutation<EInvoiceResponse, { id: string; data: InvoiceRecord }>({
      query: ({ id, data }) => ({
        url: `${urlEndpoint}/${id}/replace`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: tagType, id: 'CREATE' },
        { type: tagType, id: 'LIST' },
      ],
    }),
    updateEInvoice: builder.mutation<EInvoiceResponse, { id: string; data: Partial<InvoiceRecord> }>({
      query: ({ id, data }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: tagType, id: 'DETAIL' },
      ],
    }),

    signEInvoice: builder.mutation<EInvoiceResponse, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}/sign-invoices`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: tagType, id: 'SIGN' },
        { type: tagType, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllEInvoicesQuery,
  useGetAllTemplateEInvoicesQuery,
  useCreateEInvoiceMutation,
  useGetEInvoiceByIdQuery,
  useDownloadEInvoiceMutation,
  useSignEInvoiceMutation,
  useGetReplaceableInvoicesQuery,
  useReplaceEInvoiceMutation,
  useUpdateEInvoiceMutation,
} = eInvoiceApiService;
